'use client';

import type * as React from 'react';

import type { VariantProps } from 'class-variance-authority';
import Link, { type LinkProps } from 'next/link';

import { buttonVariants, type ResponsiveButtonSize, resolveButtonSize } from './button';
import { useRipple } from './hooks/use-ripple';
import { cn } from './lib/cn';

type LinkButtonProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps &
  VariantProps<typeof buttonVariants> & {
    size?: ResponsiveButtonSize;
    children?: React.ReactNode;
    disabled?: boolean;
  };

function LinkButton({
  className,
  appearance,
  color,
  size = 'sm',
  rounded,
  children,
  href,
  onClick,
  disabled,
  ...props
}: LinkButtonProps) {
  const { ripples, createRipple } = useRipple();

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    createRipple(event);
    onClick?.(event);
  };

  return (
    <Link
      className={cn(buttonVariants({ appearance, color, rounded }), resolveButtonSize(size), className)}
      href={href}
      onClick={handleClick}
      {...props}>
      {children}
      <span className="pointer-events-none absolute inset-0">
        {ripples.map((ripple) => (
          <span
            className="absolute animate-ripple rounded-full bg-current opacity-30"
            key={ripple.id}
            style={{
              left: ripple.x,
              top: ripple.y,
              width: ripple.size,
              height: ripple.size,
            }}
          />
        ))}
      </span>
    </Link>
  );
}

export { LinkButton };
